/**
 * Reading one server-sent-event stream — the platform's only copy.
 *
 * Every streaming surface here (the chat, the Concierge, the Agent of Agents,
 * the Guide) POSTs a message and reads SSE frames back, and each one used to
 * carry its own line-for-line copy of this loop: the auth headers off
 * sessionStorage, the `getReader()`/`TextDecoder` pump, the buffer split that
 * keeps a partial last line, the `data: ` prefix test, the `done` early
 * return, and the `finally { reader.cancel() }`. Four copies meant a fix to
 * any of them — a proxy-buffering workaround, a reconnect, an abort — had to
 * be made four times, and was not.
 *
 * `signal` is why the extraction matters rather than merely tidying: handing
 * it to `fetch` is what makes Stop instantaneous, because aborting rejects
 * the in-flight `reader.read()` and tears the socket down instead of waiting
 * for the next event so a loop-side flag can be polled. Server-side the
 * socket close IS the stop signal — it trips `progress_bus.pump_stream`'s
 * `abandoned` flag, which closes the producer and cancels the turn. A copy
 * that dropped the signal (the Guide's did) cannot be stopped at all.
 */

import api from '@services/api';

export interface SseOptions {
  signal?: AbortSignal;
}

/** Any SSE payload this platform sends: a discriminated `type`, plus fields. */
export interface SseEvent {
  type: string;
}

/**
 * POST `url` and yield each `data:` frame, parsed, until the stream ends or a
 * `done` event arrives. Malformed frames are skipped: a chunk boundary can
 * split a line, and the next read completes it.
 */
export async function* streamSSE<T extends SseEvent>(
  url: string,
  body: Record<string, unknown>,
  options?: SseOptions,
): AsyncGenerator<T, void, undefined> {
  const apiUrl = api.defaults.baseURL;
  if (!apiUrl) throw new Error('API baseURL not configured');
  const token = sessionStorage.getItem('access_token');
  const tenantId = sessionStorage.getItem('tenant_id') || '';

  const response = await fetch(`${apiUrl}${url}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      'X-Tenant-ID': tenantId,
    },
    body: JSON.stringify(body),
    signal: options?.signal,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }
  if (!response.body) throw new Error('No response body');

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      // SSE frames are separated by blank lines; events are 'data: ...' lines.
      const lines = buffer.split('\n');
      buffer = lines.pop() || ''; // keep the partial last line for the next read
      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        const jsonStr = line.slice(6);
        if (!jsonStr.trim()) continue;
        try {
          const ev = JSON.parse(jsonStr) as T;
          yield ev;
          if (ev.type === 'done') return;
        } catch {
          // A partial chunk, not a protocol error: the next read completes it.
        }
      }
    }
  } finally {
    try { reader.cancel(); } catch { /* the stream is already gone */ }
  }
}
